import { bind } from 'bind-event-listener';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { SelectionContext } from '@atlaskit/editor-common/types/selection';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type Payload } from '@atlaskit/rovo-triggers/types';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import {
	expVal,
	isExperimentEnabled,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { useContentType } from '@confluence/page-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { resolveRemixDynamicUiContentType, type DynamicUiSource } from './common-types.ts';
import { prepareRemixFragmentNodes } from './filterRemixFragmentNodes.ts';
import {
	deriveLegacyRendererRemixPhase,
	deriveMachineRendererRemixPhase,
} from './rendererRemixLifecycle.ts';
import { RendererRemixHighlightStyles } from './RendererRemixHighlightStyles.tsx';
import { useIsRemixAvailable } from './useIsRemixAvailable.ts';
import { useRemixPreviewFlow } from './useRemixPreviewFlow.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import { useRendererRemixHighlight } from './useRendererRemixHighlight.ts';
import {
	type RendererRemixMachineLifecycle,
	useRendererRemixMachineLifecycle,
} from './useRendererRemixMachineLifecycle.ts';
import { useShouldHideDiagramRemixOption } from './useShouldHideDiagramRemixOption.ts';

const constructDynamicUiSource = (
	adfDocument: DocNode,
	selectionContext: SelectionContext | null,
	contentType: DynamicUiSource['contentType'],
): DynamicUiSource | undefined => {
	const fragmentAdf = prepareRemixFragmentNodes(selectionContext?.selectionFragment ?? null);

	return {
		contentType,
		startLocalId: selectionContext?.localIds?.[0] ?? '',
		endLocalId: selectionContext?.localIds?.[selectionContext.localIds.length - 1] ?? '',
		startIndex: selectionContext?.startIndex ?? 0,
		endIndex: selectionContext?.endIndex ?? -1,
		// When the entire page is selected (e.g. cmd+a), selectionContext is null and
		// selectionFragment is not populated. Fall back to the full document content
		// so that Remix still receives the source content it needs.

		fragmentAdf: fragmentAdf ?? (adfDocument.content as any),
		fullAdf: adfDocument,
	};
};

const RENDERER_SELECTION_CONSUME_ONCE_KEY = 'renderer-selection-remix';
const publishRendererRemixHandoffFailed = () => {
	// `rovo-remix-handoff-failed` is not in the merged PubSub Payload union yet,
	// so TypeScript will not accept it as a `publish()` argument without a cast.
	publish(Topics.ROVO_REMIX, {
		type: 'rovo-remix-handoff-failed',
		source: RENDERER_SELECTION_CONSUME_ONCE_KEY,
	} as unknown as Payload);
};

const isRendererSelectionSubmit = (payload: Payload): boolean =>
	payload.type === 'submit-rovo-remix' &&
	payload.data.remixConsumer === 'renderer' &&
	payload.data.remixPreset === 'selection';

type RendererRemixStartResult =
	| { status: 'started' }
	| {
			status: 'rejected';
			reason: 'admission-rejected' | 'send-failed';
	  };

const ListenForSelectedContentRemixSubmit = ({
	currentContentId,
	hasGenerationStarted,
	onAcceptSubmit,
	onDisarm,
	originContentIdRef,
}: {
	currentContentId: string | undefined;
	hasGenerationStarted: boolean;
	onAcceptSubmit: (
		params: RemixOptionSelectionParameters & { conversationId?: string },
	) => Promise<RendererRemixStartResult>;
	onDisarm: () => void;
	originContentIdRef: React.MutableRefObject<string | undefined>;
}) => {
	const currentContentIdRef = useRef(currentContentId);
	currentContentIdRef.current = currentContentId;
	const hasGenerationStartedRef = useRef(hasGenerationStarted);
	hasGenerationStartedRef.current = hasGenerationStarted;
	const acceptedSubmitRef = useRef(false);
	const onAcceptSubmitRef = useRef(onAcceptSubmit);
	onAcceptSubmitRef.current = onAcceptSubmit;
	const onDisarmRef = useRef(onDisarm);
	onDisarmRef.current = onDisarm;

	const disarmIfIdle = useCallback(() => {
		if (hasGenerationStartedRef.current || acceptedSubmitRef.current) {
			return;
		}
		onDisarmRef.current();
	}, []);

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: RENDERER_SELECTION_CONSUME_ONCE_KEY },
		(payload) => {
			if (!isRendererSelectionSubmit(payload) || payload.type !== 'submit-rovo-remix') {
				return;
			}

			const originContentId = originContentIdRef.current;
			if (
				originContentId &&
				currentContentIdRef.current &&
				originContentId !== currentContentIdRef.current
			) {
				return;
			}

			if (acceptedSubmitRef.current || hasGenerationStartedRef.current) {
				return;
			}

			acceptedSubmitRef.current = true;
			void onAcceptSubmitRef
				.current({
					type: payload.data.remixOption,
					subType: payload.data.remixSubType,
					userPrompt: payload.data.userPrompt,
					conversationId: payload.data.conversationId,
				})
				.then((result) => {
					if (result.status === 'rejected') {
						acceptedSubmitRef.current = false;
					}
				})
				.catch(() => {
					// Keep the listener retryable if an unexpected callback failure escapes.
					acceptedSubmitRef.current = false;
				});
		},
	);

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'chat-close') {
			disarmIfIdle();
			return;
		}

		if (payload.type === 'chat-new' && payload.data.resetActiveMenu) {
			disarmIfIdle();
			return;
		}

		if (payload.type !== 'open-rovo-remix-sidebar') {
			return;
		}

		if (payload.data.remixConsumer === 'renderer' && payload.data.remixPreset === 'selection') {
			return;
		}

		disarmIfIdle();
	});

	return null;
};

/**
 * Runs the Remix clean-up when the user closes the Rovo panel before choosing a Remix
 * option.
 *
 * Closing the Rovo panel sends no message that Confluence can listen for, so the only
 * way to notice it is to watch whether the panel is currently showing.
 *
 * The same click that arms the Remix flow also asks the panel to open, so there is a
 * short moment where the panel is not showing yet. Treating that moment as a close would
 * cancel the flow the instant it started, so a close is only acted on once the panel has
 * been seen showing at least once.
 */
const CancelRemixWhenRovoPanelCloses = ({
	onRovoPanelClosed,
}: {
	onRovoPanelClosed: () => void;
}) => {
	const { isConversationAssistantSidebarActive, mode } = useToggleConversationAssistant();
	const isConversationAssistantOpen = isConversationAssistantSidebarActive || mode !== undefined;
	const hasSeenRovoPanelOpen = useRef(false);
	const onRovoPanelClosedRef = useRef(onRovoPanelClosed);
	onRovoPanelClosedRef.current = onRovoPanelClosed;

	useEffect(() => {
		if (isConversationAssistantOpen) {
			hasSeenRovoPanelOpen.current = true;
			return;
		}

		if (!hasSeenRovoPanelOpen.current) {
			return;
		}

		onRovoPanelClosedRef.current();
	}, [isConversationAssistantOpen]);

	return null;
};

type RendererRemixCreatorProps = {
	adfDocument: DocNode;
	selectionContext: SelectionContext | null;
	interactionSource?: InteractionSource;
	onClose: (options?: { preservePreviewSession?: boolean }) => void;
	onStartThinking: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
};

type RendererRemixModalProps = RendererRemixCreatorProps & {
	isEphemeralViewVisible: boolean;
	isRendererOverlayOpen: boolean;
	currentContentId: string | undefined;
	anchorRect?: DOMRect;
	anchorElement?: HTMLElement;
	isThinking: boolean;
	isMachineReadTreatment: boolean;
	machineLifecycle: RendererRemixMachineLifecycle;
};

const RendererRemixModal = ({
	adfDocument,
	selectionContext,
	interactionSource,
	onClose,
	onStartThinking,
	onStopThinking,
	isEphemeralViewVisible,
	isRendererOverlayOpen,
	currentContentId,
	anchorRect,
	anchorElement,
	isThinking,
	isMachineReadTreatment,
	machineLifecycle,
}: RendererRemixModalProps) => {
	const [currentContentType] = useContentType();
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const hiddenOptions = useShouldHideDiagramRemixOption();
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const selectedSubTypeRef = useRef<string | undefined>(undefined);
	const conversationIdRef = useRef<string | undefined>(undefined);
	const remixOriginContentIdRef = useRef<string | undefined>(undefined);
	const [positionedAnchorRect, setPositionedAnchorRect] = useState(anchorRect);
	const [popupRepositionKey, setPopupRepositionKey] = useState(0);
	const restartExperiencesAfterRejectedStart = useCallback(
		(rejectionReason: string) => {
			const restartAttributes = {
				entryPoint: 'renderer',
				remixSource: 'rejectedRemixStart',
				product: 'confluence',
				rejectionReason,
				...(interactionSource !== undefined ? { originalRemixSource: interactionSource } : {}),
			};
			hasCommittedRemixCreateTerminal.current = false;
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: restartAttributes,
			});
			startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
		},
		[experienceTracker, interactionSource],
	);

	useEffect(() => {
		if (remixOriginContentIdRef.current === undefined) {
			remixOriginContentIdRef.current = currentContentId;
		}
	}, [currentContentId]);

	useEffect(() => {
		setPositionedAnchorRect(anchorRect);
	}, [anchorRect]);

	useEffect(() => {
		if (!anchorElement) {
			return;
		}

		let animationFrameId: number | undefined;
		const updateAnchorPosition = () => {
			if (animationFrameId !== undefined) {
				window.cancelAnimationFrame(animationFrameId);
			}
			animationFrameId = window.requestAnimationFrame(() => {
				// Keep the last known rect: the anchor can detach while the picker is opening.
				if (!anchorElement.isConnected) {
					return;
				}
				setPositionedAnchorRect(anchorElement.getBoundingClientRect());
				setPopupRepositionKey((key) => key + 1);
			});
		};

		// `anchorRect` positions the initial render, so only follow on scroll/resize.
		const unbindScroll = bind(window, {
			type: 'scroll',
			listener: updateAnchorPosition,
			options: { capture: true, passive: true },
		});
		const unbindResize = bind(window, {
			type: 'resize',
			listener: updateAnchorPosition,
		});

		return () => {
			unbindScroll();
			unbindResize();
			if (animationFrameId !== undefined) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, [anchorElement]);

	const { dynamicUiType } = useStagingAreaState();
	const { openConversationAssistant } = useToggleConversationAssistant();
	const {
		startFlow,
		onPreviewOpened,
		onPreviewDismissed,
		onStopThinking: stopMachineThinking,
		onRovoAborted,
		onErrorDismissed,
		onSendRejected,
		onNavigation,
	} = machineLifecycle;

	const dismissRendererRemixUiAfterNavigation = useCallback(() => {
		const reason = 'User navigated away before renderer remix preview opened';
		isRemixCancelled.current = true;
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason,
			attributes: { reason },
		});
		abortRemixCreateExperience(experienceTracker, reason);
		void remixGeneratePreviewExperience.abort({
			metadata: { reason },
		});
		onStopThinking();
		onClose();
		onNavigation();
	}, [experienceTracker, onClose, onNavigation, onStopThinking]);

	const shouldOpenPreview = useCallback(() => {
		if (isRemixCancelled.current && expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false)) {
			return false;
		}

		if (!fg('cc-maui-phase-3')) {
			return true;
		}

		const originContentId = remixOriginContentIdRef.current;

		return !originContentId || !currentContentId || originContentId === currentContentId;
	}, [currentContentId]);

	// Collapse the highlight overlay after the preview opens.
	const onAfterOpenPreview = useCallback(() => {
		onClose({ preservePreviewSession: true });
	}, [onClose]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		onActionReady,
		resetCompletion,
	} = useRemixPreviewFlow({
		entryPoint: 'renderer',
		experienceTracker,
		dynamicUiType,
		selectedSubTypeRef,
		openAIFCStagingModalRef,
		isRemixCancelledRef: isRemixCancelled,
		conversationIdRef,
		shouldOpenPreview,
		onAfterOpenPreview,
	});

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixCancelled: wasRemixExplicitlyCancelled,
		remixStoreError,
		openAIFCStagingModal,
		closeAIFCStagingModal,
		resetRemixConversationId,
	} = useRemixToAIFC({
		onActionReady,
		onPreviewOpened,
		onPreviewDismissed,
		trackRemixConversationById: true,
	});
	openAIFCStagingModalRef.current = openAIFCStagingModal;
	const rendererPhase = isMachineReadTreatment
		? deriveMachineRendererRemixPhase(machineLifecycle.phase)
		: deriveLegacyRendererRemixPhase({
				isThinking,
				isReady: isComplete,
				isPreviewVisible: isEphemeralViewVisible,
				isError: isRemixError,
				isAborted: isRemixAborted,
				isCancelled: wasRemixExplicitlyCancelled,
			});
	// Preserve the legacy progress contract in control. Legacy `isThinking` remains true
	// through the normal ready interval, but unlike the normalized phase it does not revive
	// progress from stale completion state after a stopped flow reopens the picker.
	const isRendererRemixInProgress = isMachineReadTreatment
		? rendererPhase === 'thinking' || rendererPhase === 'ready'
		: isThinking;
	const hasGenerationError = rendererPhase === 'error';
	const wasGenerationAborted = rendererPhase === 'aborted';

	useEffect(() => {
		if (!fg('cc-maui-phase-3')) {
			return;
		}

		const originContentId = remixOriginContentIdRef.current;

		if (!originContentId || !currentContentId || originContentId === currentContentId) {
			return;
		}

		dismissRendererRemixUiAfterNavigation();
	}, [currentContentId, dismissRendererRemixUiAfterNavigation]);

	// Abort experience on unmount (overlay closed without selecting an option).
	// Uses a ref so the cleanup always calls the latest tracker reference.
	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasCommittedRemixCreateTerminal.current,
			);
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				// Clear stale completion state from any previous flow.
				resetCompletion();
				onStartThinking();
			} else {
				isRemixCancelled.current = true;
				// A stopped flow can already have reached the completed-progress interval.
				// Clear it before reopening so control does not render a stale ready picker.
				resetCompletion();
				// Abort the current experience before stopping
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
				void remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				remixOriginContentIdRef.current = undefined;
				try {
					stopRemixing();
				} finally {
					stopMachineThinking();
				}
				if (fg('platform_editor_ai_remix_close_modal_on_stop')) {
					onClose();
				} else {
					// Start a new experience for the reopened overlay
					const restartAttributes = {
						entryPoint: 'renderer',
						remixSource: 'cancelledRemixThinking',
						originalRemixSource: 'selectionToolbar',
						product: 'confluence',
					};
					experienceTracker.start({
						name: ExperienceName.REMIX_START,
						id: ExperienceName.REMIX_START,
						attributes: restartAttributes,
					});
					startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
					onStopThinking({ reopenModal: true });
				}
			}
		},
		[
			onStartThinking,
			stopRemixing,
			stopMachineThinking,
			onClose,
			onStopThinking,
			experienceTracker,
			resetCompletion,
		],
	);

	// Fail experience and clean up thinking UI when Rovo encounters an error
	const prevHasGenerationError = useRef(hasGenerationError);
	useEffect(() => {
		if (!prevHasGenerationError.current && hasGenerationError && !isRemixCancelled.current) {
			const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
					errorStatusCode: remixStoreError?.status_code,
				},
			});
			void remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			onStopThinking({ reopenModal: true });
		}
		prevHasGenerationError.current = hasGenerationError;
	}, [hasGenerationError, remixStoreError, onStopThinking, experienceTracker]);

	// When an error occurs during remix, close the thinking bar and ephemeral
	// preview, then open the Rovo chat sidebar so the user can see the error.
	useEffect(() => {
		if (!isRemixCancelled.current && hasGenerationError) {
			// Close the ephemeral preview (staging modal) if it is open
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			// Close the thinking bar and dismiss the remix UI
			onStopThinking();
			onClose();
			// Open the Rovo chat sidebar to display the error information.
			// `openConversationAssistant` is idempotent (no-op if already
			// open), so it is safe to call on every render where
			// the selected phase is `error` — no infinite toggle loop.
			openConversationAssistant('sidebar');
			resetRemixConversationId();
			onErrorDismissed();
		}
	}, [
		hasGenerationError,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		openConversationAssistant,
		resetRemixConversationId,
		onErrorDismissed,
	]);

	// Dismiss the thinking bar and clear the selection highlight when the user
	// stops the remix from the Rovo side chat.
	useEffect(() => {
		if (wasGenerationAborted && !isRemixCancelled.current) {
			const reason = 'User stopped from Rovo side chat';
			if (expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false)) {
				isRemixCancelled.current = true;
				resetCompletion();
				// The modal store visibility can lag behind action readiness for
				// non-streaming remix types. Closing is idempotent, so do it even
				// when the render-time visibility snapshot is still false.
				closeAIFCStagingModal();
			} else if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			abortRemixCreateExperience(experienceTracker, reason);
			onStopThinking();
			onClose();
			onRovoAborted();
		}
	}, [
		wasGenerationAborted,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		experienceTracker,
		onRovoAborted,
		resetCompletion,
	]);

	const handleOptionSelect = useCallback(
		async ({
			type,
			subType,
			userPrompt,
			conversationId,
		}: RemixOptionSelectionParameters & {
			conversationId?: string;
		}): Promise<RendererRemixStartResult> => {
			// Only a submission from the Rovo sidebar carries a conversationId, and the Rovo
			// sidebar is only shown to the variant2 cohort. Its `userPrompt` is already the
			// complete sentence shown in the chat input, so passing that sentence as
			// `promptText` stops `sendAIFCPrompt` from writing a second copy of the same
			// sentence and joining the two together. Checking the cohort as well as the
			// conversationId guarantees that anyone outside the experiment keeps the original
			// behaviour, even if some future caller starts supplying a conversationId.
			const isRovoChatSubmission =
				conversationId !== undefined &&
				UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
			remixOriginContentIdRef.current = remixOriginContentIdRef.current ?? currentContentId;
			selectedSubTypeRef.current = subType;

			const dynamicUiSource = constructDynamicUiSource(
				adfDocument,
				selectionContext,
				resolveRemixDynamicUiContentType(currentContentType),
			);
			let authoritativeConversationId = conversationId;
			if (isMachineReadTreatment) {
				const admission = startFlow({
					conversationId,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					source: interactionSource,
				});
				if (!admission.admitted) {
					if (admission.reason !== 'active-flow') {
						const admissionError = new Error(
							`Renderer Remix machine admission was rejected: ${admission.reason}`,
						);
						experienceTracker.fail({
							name: ExperienceName.REMIX_START,
							error: admissionError,
							attributes: {
								reason: admissionError.message,
								rejectionReason: admission.reason,
							},
						});
						abortRemixCreateExperience(experienceTracker, admissionError.message);
						restartExperiencesAfterRejectedStart(admission.reason);
					}
					onStopThinking({ reopenModal: true });
					return { status: 'rejected', reason: 'admission-rejected' };
				}
				authoritativeConversationId = admission.conversationId;
			}

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type, remixSubtype: subType, includesFreegenPrompt: !!userPrompt },
			});
			void remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				entryPoint: 'renderer',
				remixType: type,
				remixSubtype: subType,
				includesFreegenPrompt: !!userPrompt,
				...(type === 'infographic'
					? {
							isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
						}
					: {}),
			});
			let conversationChannelId: string;
			try {
				conversationChannelId = await sendAIFCPrompt({
					userPrompt: isRovoChatSubmission ? undefined : userPrompt,
					promptText: isRovoChatSubmission ? userPrompt : undefined,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource,
					...(authoritativeConversationId !== undefined
						? { conversationId: authoritativeConversationId }
						: {}),
				});
			} catch (error) {
				onSendRejected();
				if (
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
				) {
					publishRendererRemixHandoffFailed();
				}
				const sendError =
					error instanceof Error ? error : new Error('Failed to send renderer Remix prompt');
				const reason = 'Failed to send renderer Remix prompt';
				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: sendError,
					attributes: {
						reason,
						rejectionReason: 'send-failed',
					},
				});
				void remixGeneratePreviewExperience.failure({
					metadata: { reason },
				});
				abortRemixCreateExperience(experienceTracker, reason);
				restartExperiencesAfterRejectedStart('send-failed');
				onStopThinking({ reopenModal: true });
				return { status: 'rejected', reason: 'send-failed' };
			}
			// Stash the conversationId so `useRemixPreviewFlow` can include it as
			// `conversationChannelId` on the REMIX_RENDERS_IN_MODAL start attributes
			// once `onActionReady` fires.
			conversationIdRef.current = conversationChannelId;
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { conversationChannelId },
			});
			hasCommittedRemixCreateTerminal.current = true;
			trackRemixCreateOptionSelected({
				experienceTracker,
				conversationId: conversationChannelId,
				remixType: type,
				remixSubtype: subType,
				includesFreegenPrompt: !!userPrompt,
			});
			return { status: 'started' };
		},
		[
			sendAIFCPrompt,
			adfDocument,
			selectionContext,
			currentContentType,
			interactionSource,
			experienceTracker,
			currentContentId,
			isMachineReadTreatment,
			startFlow,
			onSendRejected,
			onStopThinking,
			restartExperiencesAfterRejectedStart,
		],
	);

	const acceptRovoRemixSubmit = useCallback(
		(
			params: RemixOptionSelectionParameters & { conversationId?: string },
		): Promise<RendererRemixStartResult> => {
			onRemixFlowActiveChange(true);
			return handleOptionSelect(params);
		},
		[handleOptionSelect, onRemixFlowActiveChange],
	);

	const shouldShowOriginalRemixPopUpWindow =
		isRendererRemixInProgress ||
		!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');

	return (
		<>
			{isRendererOverlayOpen &&
				UNSAFE_expValEqualsNoExposure(
					'cc-ce-maui-remix-menu-multivariant',
					'cohort',
					'variant2',
				) && (
					<ListenForSelectedContentRemixSubmit
						currentContentId={currentContentId}
						hasGenerationStarted={isRendererRemixInProgress}
						onAcceptSubmit={acceptRovoRemixSubmit}
						onDisarm={onClose}
						originContentIdRef={remixOriginContentIdRef}
					/>
				)}
			{isRendererOverlayOpen && shouldShowOriginalRemixPopUpWindow && (
				<RemixContainer
					onClose={() => onClose()}
					hiddenOptions={hiddenOptions}
					onOptionSelect={handleOptionSelect}
					onRemixFlowActiveChange={onRemixFlowActiveChange}
					isEphemeralViewVisible={isEphemeralViewVisible}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					// Classic view page: center the fixed thinking bar over the rendered content area.
					// Safe here because the view page has a stable layout (unlike the editor, whose
					// reflows would make the one-shot content measurement drift).
					shouldMeasureContentBounds
					// The view floating toolbar provides a live element that is remeasured as it moves.
					// Snapshot-only renderer entries, such as the nudge, still close on external scroll.
					anchorRect={positionedAnchorRect}
					popupRepositionKey={popupRepositionKey}
					shouldCloseOnScroll={!anchorElement}
					isThinking={
						isMachineReadTreatment
							? isRendererRemixInProgress
							: isRendererRemixInProgress &&
								  UNSAFE_expValEqualsNoExposure(
										'cc-ce-maui-remix-menu-multivariant',
										'cohort',
										'variant2',
								  )
								? true
								: undefined
					}
					// The nudge icon sits at the top-right of a block; flying the popup out to the right
					// would frequently clip against the viewport edge, so it opens to the left instead.
					popupPlacement={
						interactionSource === 'remixModalFromViewPageNudge' ? 'bottom-end' : 'bottom-start'
					}
				/>
			)}
		</>
	);
};

export type RemixDisabledReason = 'archived' | 'version-history';

type RendererRemixContextProps = {
	openRemixModal: (
		adfDocument: DocNode,
		selection: SelectionContext | null,
		interactionSource?: InteractionSource,
		anchorRect?: DOMRect,
		anchorElement?: HTMLElement,
	) => void;
	closeRemixModal: () => void;
	clearRemixHighlight: () => void;
	isRemixDisabled: boolean;
	isRemixing: boolean;
	isRemixInProgress: boolean;
	remixDisabledReason?: RemixDisabledReason;
	registerOnModalClose: (callback: () => void) => () => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
	closeRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: false,
	isRemixing: false,
	isRemixInProgress: false,
	registerOnModalClose: () => () => {},
});

export const useRendererRemixContext = (): RendererRemixContextProps => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({
	children,
	contentId,
}: {
	children: React.ReactNode;
	contentId?: string;
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [adfDocument, setAdfDocument] = useState<DocNode | null>(null);
	const [selectionContext, setSelectionContext] = useState<SelectionContext | null>(null);
	const [isThinking, setIsThinking] = useState(false);
	const [isMachineReadTreatment, setIsMachineReadTreatment] = useState(false);
	const [anchorRect, setAnchorRect] = useState<DOMRect | undefined>(undefined);
	const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
	const interactionSourceRef = useRef<InteractionSource | undefined>(undefined);
	const { startHighlight, clearHighlight } = useRendererRemixHighlight();
	const machineLifecycle = useRendererRemixMachineLifecycle();
	const isRemixAvailable = useIsRemixAvailable();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const onModalCloseCallbacks = useRef<Set<() => void>>(new Set());

	const registerOnModalClose = useCallback((callback: () => void) => {
		onModalCloseCallbacks.current.add(callback);
		return () => {
			onModalCloseCallbacks.current.delete(callback);
		};
	}, []);
	const clearModalSession = useCallback(() => {
		setAdfDocument(null);
		setSelectionContext(null);
		setIsMachineReadTreatment(false);
		interactionSourceRef.current = undefined;
		setAnchorRect(undefined);
		setAnchorElement(undefined);
	}, []);
	const prevShowSmartCreationModal = useRef(showSmartCreationModal);

	useEffect(() => {
		if (prevShowSmartCreationModal.current && !showSmartCreationModal) {
			clearHighlight();
			// Smart Creation owns the preview and can close outside this container's closeModal path.
			// Release the renderer session when that external modal-store transition completes.
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			clearModalSession();
		}
		prevShowSmartCreationModal.current = showSmartCreationModal;
	}, [showSmartCreationModal, clearHighlight, clearModalSession]);

	const openModal = useCallback(
		(
			newAdfDocument: DocNode,
			selection: SelectionContext | null,
			interactionSource?: InteractionSource,
			newAnchorRect?: DOMRect,
			newAnchorElement?: HTMLElement,
		) => {
			setIsMachineReadTreatment(
				isRemixAvailable &&
					fg('remix_state_rearchitecture_enabled') &&
					isExperimentEnabled('remix_state_rearchitecture_renderer'),
			);
			setAdfDocument(newAdfDocument);
			startHighlight();
			setSelectionContext(selection);
			interactionSourceRef.current = interactionSource;
			setAnchorRect(newAnchorRect);
			setAnchorElement(newAnchorElement);
			setIsModalOpen(true);
		},
		[isRemixAvailable, startHighlight],
	);

	const closeModal = useCallback(
		(options?: { preservePreviewSession?: boolean }) => {
			if (!options?.preservePreviewSession) {
				clearHighlight();
			}
			onModalCloseCallbacks.current.forEach((cb) => cb());
			setIsModalOpen(false);
			setIsThinking(false);
			// Treatment keeps the phase-consuming modal subtree mounted while the Smart Creation
			// preview is open, so watcher terminal phases can drive renderer UX before teardown.
			if (!options?.preservePreviewSession || !isMachineReadTreatment) {
				clearModalSession();
			}
		},
		[clearHighlight, clearModalSession, isMachineReadTreatment],
	);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const rendererPhase = isMachineReadTreatment
		? deriveMachineRendererRemixPhase(machineLifecycle.phase)
		: deriveLegacyRendererRemixPhase({
				isThinking,
				isReady: false,
				isPreviewVisible: showSmartCreationModal,
				isError: false,
				isAborted: false,
				isCancelled: false,
			});

	const context = useMemo(() => {
		const isRemixInProgress = rendererPhase === 'thinking' || rendererPhase === 'ready';
		const isRemixing =
			isModalOpen ||
			showSmartCreationModal ||
			isRemixInProgress ||
			rendererPhase === 'previewing' ||
			rendererPhase === 'submitting';
		return {
			openRemixModal: openModal,
			closeRemixModal: closeModal,
			clearRemixHighlight: clearHighlight,
			isRemixDisabled: false,
			// `isRemixing` means any Remix UI is active/open. `isRemixInProgress` means
			// generation has started and the trigger should be disabled/greyed out.
			isRemixing,
			isRemixInProgress,
			registerOnModalClose,
		};
	}, [
		openModal,
		closeModal,
		clearHighlight,
		isModalOpen,
		rendererPhase,
		showSmartCreationModal,
		registerOnModalClose,
	]);

	return (
		<RendererRemixContext.Provider value={context}>
			<RendererRemixHighlightStyles />
			{children}
			{/*
			    This is only needed by the people in the cc-ce-maui-remix-menu-multivariant
			    experiment who see the list of Remix options inside the Rovo panel. Everyone else
			    sees those options in a pop-up window that Confluence opens and closes itself, so
			    Confluence already knows when that window has gone away.

			    It is only added to the page while the person is still choosing an option and
			    nothing has been written yet, so a Remix that has already started writing is
			    never cancelled.

			    It is placed here rather than inside ConfluenceConversationAssistant just below,
			    because that part of the page is only downloaded the first time it is needed, and
			    anything inside it would start watching too late to notice the panel closing.
			*/}
			{isModalOpen &&
				!isThinking &&
				UNSAFE_expValEqualsNoExposure(
					'cc-ce-maui-remix-menu-multivariant',
					'cohort',
					'variant2',
				) && <CancelRemixWhenRovoPanelCloses onRovoPanelClosed={closeModal} />}
			{(isModalOpen || isThinking || isMachineReadTreatment) && adfDocument && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal
						adfDocument={adfDocument}
						selectionContext={selectionContext}
						interactionSource={interactionSourceRef.current}
						onClose={closeModal}
						onStartThinking={onStartThinking}
						onStopThinking={onStopThinking}
						isEphemeralViewVisible={showSmartCreationModal}
						isRendererOverlayOpen={isModalOpen || isThinking}
						currentContentId={contentId}
						anchorRect={anchorRect}
						anchorElement={anchorElement}
						isThinking={isThinking}
						isMachineReadTreatment={isMachineReadTreatment}
						machineLifecycle={machineLifecycle}
					/>
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

const makeDisabledRemixContextValue = (
	reason?: RemixDisabledReason,
): RendererRemixContextProps => ({
	openRemixModal: () => {},
	closeRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: true,
	isRemixing: false,
	isRemixInProgress: false,
	remixDisabledReason: reason,
	registerOnModalClose: () => () => {},
});

const disabledRemixContextValue = makeDisabledRemixContextValue();

export const RendererRemixContainer = ({
	children,
	contentId,
	isDisabled,
	remixDisabledReason,
}: {
	children: React.ReactNode;
	contentId?: string;
	isDisabled?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element => {
	if (isDisabled) {
		const contextValue = remixDisabledReason
			? makeDisabledRemixContextValue(remixDisabledReason)
			: disabledRemixContextValue;
		return (
			<RendererRemixContext.Provider value={contextValue}>{children}</RendererRemixContext.Provider>
		);
	}

	return <RendererRemixContainerImpl contentId={contentId}>{children}</RendererRemixContainerImpl>;
};
